import type { ColDef } from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { type RefObject, useEffect, useMemo } from "react"

import {
  useRefreshColumnsOnDataChange,
  type VersionedColumns,
} from "~/hooks/useRefreshColumnsOnDataChange"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { detailGridApis, repaintColumns } from "~/utils/ag-utils"
import { ProductTableColumn } from "~/utils/productTableUtils"

import type { ForecastGridContext } from "./columns/forecast"
import { STOCK_COLUMN_SPECS, type StockGridContext } from "./columns/stock"
import type { ColumnNamingId } from "./utils/columnUtils"

export type ProductTableGridContext = StockGridContext & ForecastGridContext

/**
 * The product table's AG Grid `context`, and the repaints that follow a change to it, since AG
 * Grid re-reads the context only when it evaluates a cell.
 */
export function useProductTableGridContext({
  gridRef,
  warehouseId,
  getSkuForecastsBetweenDates,
  alertsUnavailable,
  forecastMetadata,
  netForecastMode,
  netForecastValueGetter,
  netForecastPending,
  forecastAlertThreshold,
  evaluationMap,
  stockColumn,
  forecastColumn,
  versionedColumns,
}: {
  gridRef: RefObject<AgGridReact<IVariantSummary> | null>
  stockColumn: (colId: ColumnNamingId) => ColDef<IVariantSummary>
  forecastColumn: (colId: ColumnNamingId) => ColDef<IVariantSummary>
  /** Further columns that read data outside the rows, repainted in the same pass. */
  versionedColumns: readonly (VersionedColumns<IVariantSummary> | undefined)[]
} & ProductTableGridContext): ProductTableGridContext {
  const stockContext = useMemo(
    (): StockGridContext => ({ warehouseId, getSkuForecastsBetweenDates }),
    [warehouseId, getSkuForecastsBetweenDates],
  )
  const netDemand = useMemo(
    () => ({ netForecastMode, netForecastValueGetter, netForecastPending }),
    [netForecastMode, netForecastValueGetter, netForecastPending],
  )
  const context = useMemo(
    (): ProductTableGridContext => ({
      ...stockContext,
      ...netDemand,
      alertsUnavailable,
      forecastMetadata,
      forecastAlertThreshold,
      evaluationMap,
    }),
    [
      stockContext,
      netDemand,
      alertsUnavailable,
      forecastMetadata,
      forecastAlertThreshold,
      evaluationMap,
    ],
  )

  // SKU detail grids reuse these column definitions and copy the context when they open; they
  // take the new one before the repaints below read it.
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
    const masterContext = api.getGridOption("context")
    for (const detailApi of detailGridApis(api)) {
      detailApi.setGridOption("context", masterContext)
      detailApi.refreshHeader()
    }
  }, [context])

  // Cells only: a scope change rebuilds every row, so an aggregate pass would run a second time.
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
    for (const gridApi of [api, ...detailGridApis(api)]) {
      repaintColumns(gridApi, [ProductTableColumn.ForecastedGrowthAlert])
    }
  }, [alertsUnavailable, gridRef])

  // Grid context and columns read outside the rows change without the rows changing.
  useRefreshColumnsOnDataChange(gridRef, [
    {
      columns: STOCK_COLUMN_SPECS.map((spec) => stockColumn(spec.colId)),
      dataVersion: stockContext,
    },
    { columns: [forecastColumn(ProductTableColumn.Forecast)], dataVersion: netDemand },
    { columns: [forecastColumn(ProductTableColumn.ForecastModel)], dataVersion: forecastMetadata },
    {
      columns: [forecastColumn(ProductTableColumn.ForecastedGrowthAlert)],
      dataVersion: forecastAlertThreshold,
    },
    { columns: [forecastColumn(ProductTableColumn.ForecastQuality)], dataVersion: evaluationMap },
    ...versionedColumns,
  ])

  return context
}
